import { PhoneIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Text } from '@/components/ui/catalyst/text';
import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { getIntervalLabel, getNextCallStatus } from '@/utils/voiceAiScheduling';

import { useVoiceAIStatus } from './hooks/useVoiceAIStatus';

interface VoiceAIStatusIndicatorProps {
  request: TreatmentRecordRequestSchema;
  className?: string;
}

export const VoiceAIStatusIndicator: React.FC<VoiceAIStatusIndicatorProps> = ({
  request,
  className,
}) => {
  const voiceAI = useVoiceAIStatus(request);

  if (voiceAI.isPortal) {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        <Badge color="zinc">Not applicable</Badge>
        <Text className="text-xs text-gray-500">
          Portal requests tracked automatically
        </Text>
      </div>
    );
  }

  const { message, isScheduled } = getNextCallStatus(
    voiceAI.nextCallDate,
    request.channel?.type,
    request.status,
  );

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <PhoneIcon
        className={clsx('h-4 w-4', {
          'text-green-600': voiceAI.isActive,
          'text-blue-600': voiceAI.willBeActive,
          'text-gray-400': !voiceAI.isActive && !voiceAI.willBeActive,
        })}
      />
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Text className="text-sm font-medium">
            {getIntervalLabel(voiceAI.interval)}
          </Text>
          {isScheduled && (
            <Badge color={voiceAI.isActive ? 'green' : 'blue'}>{message}</Badge>
          )}
        </div>
        {!isScheduled && (
          <Text className="text-xs text-gray-500">{message}</Text>
        )}
      </div>
    </div>
  );
};
